import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, X, Plus, Minus, Menu } from "lucide-react";
import { useCart } from "../context/CartContext";
import { fmtPrice, api } from "../lib/api";
import { scrollToId } from "../lib/scroll";
import { toast } from "sonner";
import logo from "../assets/logo.png";

export const Header = () => {
  const { count, setOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { label: "Fleet", to: "/#fleet" },
    { label: "Story", to: "/#story" },
    { label: "Shipping", to: "/#shipping" },
    { label: "Contact", to: "/#contact" },
  ];

  const go = (to) => {
    setMenuOpen(false);
    if (to.startsWith("/#")) {
      navigate("/");
      setTimeout(() => scrollToId(to.slice(2)), 120);
    } else navigate(to);
  };

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/70 border-b"
        style={{ borderColor: "var(--ggc-border)" }}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
          <button onClick={() => go("/")} className="flex items-center gap-3 group" data-testid="logo-btn">
            <img src={logo} alt="Global Golf Carts LLC" className="h-12 w-12 object-contain" />
            <span className="font-label uppercase tracking-[0.28em] text-sm text-[var(--ggc-text)] group-hover:text-gold transition-colors hidden sm:inline">
              Global Golf Carts
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <button
                key={l.label}
                onClick={() => go(l.to)}
                className="font-label uppercase tracking-[0.2em] text-xs text-[var(--ggc-muted)] hover:text-gold transition-colors"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setOpen(true)}
              className="relative p-2 hover:text-gold transition-colors"
              data-testid="open-cart-btn"
              aria-label="Open cart"
            >
              <ShoppingBag size={20} strokeWidth={1.4} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-gold text-black text-[10px] font-semibold rounded-full w-4 h-4 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <button
              className="md:hidden p-2"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              {menuOpen ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden overflow-hidden border-t"
              style={{ borderColor: "var(--ggc-border)" }}
            >
              <div className="px-6 py-4 flex flex-col gap-4 bg-black/90">
                {links.map((l) => (
                  <button
                    key={l.label}
                    onClick={() => go(l.to)}
                    className="text-left font-label uppercase tracking-[0.2em] text-sm text-[var(--ggc-muted)] hover:text-gold"
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      <CartDrawer />
    </>
  );
};

const CartDrawer = () => {
  const { items, removeItem, updateQty, total, open, setOpen } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/70 z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[440px] z-[70] bg-[var(--ggc-surface)] border-l flex flex-col"
            style={{ borderColor: "var(--ggc-border)" }}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-[76px] border-b" style={{ borderColor: "var(--ggc-border)" }}>
              <span className="font-label uppercase tracking-[0.24em] text-sm">Your Selection</span>
              <button onClick={() => setOpen(false)} className="hover:text-gold" data-testid="close-cart-btn">
                <X size={22} strokeWidth={1.4} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {items.length === 0 && (
                <p className="text-[var(--ggc-muted)] font-body text-sm mt-10 text-center" data-testid="empty-cart-msg">
                  Your selection is empty. Explore the fleet to begin.
                </p>
              )}
              {items.map((i) => (
                <div key={i.lookup_key} className="flex gap-4" data-testid={`cart-item-${i.lookup_key}`}>
                  <img src={i.image} alt={i.name} className="w-24 h-20 object-cover rounded-sm border" style={{ borderColor: "var(--ggc-border)" }} />
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <p className="font-serif-ed text-base">{i.name}</p>
                      <button onClick={() => removeItem(i.lookup_key)} className="text-[var(--ggc-muted)] hover:text-gold">
                        <X size={16} />
                      </button>
                    </div>
                    <p className="text-gold font-body text-sm mt-1">{fmtPrice(i.price)}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <button onClick={() => updateQty(i.lookup_key, -1)} className="w-6 h-6 border rounded-sm flex items-center justify-center hover:border-[var(--ggc-gold)]" style={{ borderColor: "var(--ggc-border)" }}>
                        <Minus size={12} />
                      </button>
                      <span className="font-body text-sm w-4 text-center">{i.quantity}</span>
                      <button onClick={() => updateQty(i.lookup_key, 1)} className="w-6 h-6 border rounded-sm flex items-center justify-center hover:border-[var(--ggc-gold)]" style={{ borderColor: "var(--ggc-border)" }}>
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-6 border-t" style={{ borderColor: "var(--ggc-border)" }}>
              <div className="flex justify-between mb-4">
                <span className="font-label uppercase tracking-[0.2em] text-xs text-[var(--ggc-muted)]">Subtotal</span>
                <span className="font-serif-ed text-xl text-gold" data-testid="cart-total">{fmtPrice(total)}</span>
              </div>
              <button
                onClick={checkout}
                disabled={!items.length || loading}
                className="w-full bg-gold text-black font-label uppercase tracking-[0.2em] text-sm py-4 rounded-sm disabled:opacity-40 hover:bg-[var(--ggc-gold-hover)] transition-colors"
                data-testid="checkout-btn"
              >
                {loading ? "Redirecting…" : "Proceed to Checkout"}
              </button>
              <p className="text-[var(--ggc-muted)] text-xs font-body text-center mt-3">
                Complimentary nationwide delivery • Secure Stripe checkout
              </p>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
